import { useState, FormEvent } from "react";
import Toast from "@/components/base/Toast";
import { usePhoneMask } from "@/hooks/usePhoneMask";
import { home, site } from "@/content";
import { Rich } from "@/lib/rich";

export default function LightContact() {
  const { contact } = home;
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [toast, setToast] = useState({
    message: "",
    type: "success" as "success" | "error",
    visible: false,
  });
  const phoneMask = usePhoneMask();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const textarea = formEl.querySelector("textarea");

    if (textarea && textarea.value.length > 500) {
      setToast({
        message: contact.form.errors.tooLong,
        type: "error",
        visible: true,
      });
      return;
    }

    setLoading(true);
    const data = new URLSearchParams(
      new FormData(formEl) as unknown as Record<string, string>
    );
    data.append("page_path", window.location.pathname);
    data.append("form_name", "light-contact-form");

    try {
      await fetch(site.webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setToast({
        message: contact.form.errors.success,
        type: "success",
        visible: true,
      });
      formEl.reset();
      setCharCount(0);
    } catch {
      setToast({
        message: contact.form.errors.generic,
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const socials = site.socials;

  return (
    <section id="contact" className="py-24 bg-[#FAFAF8] dark:bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute top-[5%] right-[0%] w-[400px] h-[400px] rounded-full bg-orange-100/50 dark:bg-orange-500/[0.08] blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[10%] left-[-5%] w-[300px] h-[300px] rounded-full bg-amber-100/40 dark:bg-amber-500/[0.08] blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-stone-100 dark:bg-white/[0.06] border border-stone-200 dark:border-white/[0.10] text-stone-600 dark:text-white/70 text-xs font-semibold tracking-widest uppercase mb-6">
              {contact.badge}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 dark:text-white leading-tight mb-6">
              <Rich text={contact.title} accentClassName="text-orange-700 dark:text-orange-400" />
            </h2>
            <p className="text-stone-600 dark:text-white/65 text-lg leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
              {contact.text}
            </p>

            <div className="flex flex-col gap-5 text-stone-600 dark:text-white/60 text-sm mb-8">
              {contact.bullets.map((bullet) => (
                <div
                  key={bullet.text}
                  className="flex items-center gap-3 justify-center lg:justify-start"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-white/[0.06]">
                    <i className={`${bullet.icon} text-stone-600 dark:text-white/60 text-sm`}></i>
                  </div>
                  <span>{bullet.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 text-stone-600 dark:text-white/60 text-sm">
              <a
                href={site.contacts.emailHref}
                className="flex items-center gap-2 justify-center lg:justify-start hover:text-stone-700 dark:hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-sm"></i>
                {site.contacts.email}
              </a>
              <a
                href={site.contacts.phoneHref}
                className="flex items-center gap-2 justify-center lg:justify-start hover:text-stone-700 dark:hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-sm"></i>
                {site.contacts.phone}
              </a>
            </div>

            <div className="flex gap-3 mt-6 justify-center lg:justify-start">
              {socials.map((social) => (
                <a
                  key={social.key}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 dark:bg-white/[0.06] text-stone-600 dark:text-white/60 hover:bg-stone-800 dark:hover:bg-orange-500 hover:text-white dark:hover:text-[#0A0A0A] transition-all cursor-pointer"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="flex-1 w-full max-w-lg">
            <div className="bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/[0.08] dark:backdrop-blur-xl rounded-3xl p-8 md:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <div className="mb-6">
                <div className="text-stone-900 dark:text-white font-bold text-lg mb-1">
                  {contact.formTitle}
                </div>
                <div className="text-stone-600 dark:text-white/65 text-sm">{contact.formSubtitle}</div>
              </div>

              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-stone-600 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {contact.form.fields.name}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={contact.form.fields.namePlaceholder}
                    className="w-full bg-stone-50 dark:bg-white/[0.05] border border-stone-200 dark:border-white/[0.14] rounded-xl px-4 py-3 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-white/40 text-sm focus:outline-none focus:border-stone-400 dark:focus:border-orange-500/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {contact.form.fields.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={contact.form.fields.emailPlaceholder}
                    className="w-full bg-stone-50 dark:bg-white/[0.05] border border-stone-200 dark:border-white/[0.14] rounded-xl px-4 py-3 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-white/40 text-sm focus:outline-none focus:border-stone-400 dark:focus:border-orange-500/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {contact.form.fields.phone}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder={contact.form.fields.phonePlaceholder}
                    {...phoneMask}
                    className="w-full bg-stone-50 dark:bg-white/[0.05] border border-stone-200 dark:border-white/[0.14] rounded-xl px-4 py-3 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-white/40 text-sm focus:outline-none focus:border-stone-400 dark:focus:border-orange-500/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {contact.form.fields.company}
                  </label>
                  <input
                    type="text"
                    name="company"
                    placeholder={contact.form.fields.companyPlaceholder}
                    className="w-full bg-stone-50 dark:bg-white/[0.05] border border-stone-200 dark:border-white/[0.14] rounded-xl px-4 py-3 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-white/40 text-sm focus:outline-none focus:border-stone-400 dark:focus:border-orange-500/40 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-stone-600 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                    {contact.form.fields.message}
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder={contact.form.fields.messagePlaceholder}
                    onChange={(event) => setCharCount(event.target.value.length)}
                    className="w-full bg-stone-50 dark:bg-white/[0.05] border border-stone-200 dark:border-white/[0.14] rounded-xl px-4 py-3 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-white/40 text-sm focus:outline-none focus:border-stone-400 dark:focus:border-orange-500/40 transition-colors resize-none"
                  />
                  <div className="text-right text-stone-600 dark:text-white/60 text-xs mt-1">
                    {charCount}/500
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full py-4 rounded-2xl bg-stone-900 dark:bg-orange-500 text-white dark:text-[#0A0A0A] font-bold text-sm hover:bg-stone-800 dark:hover:bg-orange-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? contact.form.submittingLabel : contact.form.submitLabel}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={() => setToast((state) => ({ ...state, visible: false }))}
      />
    </section>
  );
}
